/**
 * tourCart.js
 *
 * Routes Evey Events & Tickets "Add to cart" submissions through the theme's
 * AJAX cart + drawer instead of the default full-page POST to /cart.
 *
 * Evey renders its own <form class="evey__multi-variant-selector"> that posts
 * to /cart/add. The theme's product-form listeners don't recognise it (it has
 * no [data-product-form] / [data-product-submit] hooks), so it falls back to a
 * native submit and lands on /cart. This module intercepts that submit,
 * performs an AJAX multi-item add, then refreshes + opens the cart drawer —
 * exactly like productForm.js does for standard products.
 *
 * Detection is by form class, so this covers the Brewery Tour product and any
 * future Evey ticket product without hardcoding a handle.
 *
 * @version 1.0.0
 */

import { openDrawer } from './drawer.js';
import { updateUI as updateCartUI } from './cartDrawer.js';

/* ----------------------------------------------------------------------- */
/*  DOM selectors                                                          */
/* ----------------------------------------------------------------------- */

const SEL = {
	drawer: '[data-cart-drawer]',
	eveyForm: 'form.evey__multi-variant-selector',
	variantContainer: '.evey__variant-container',
	variantId: 'input[name="items[][id]"]',
	variantQty: 'input[name="items[][quantity]"]',
	lineProps: 'input[name^="items[][properties]"]',
	formProps: 'input[name^="properties["]',
	submitBtn: 'button[type="submit"], .product-form__submit',
	messageArea: '.evey__product-message',
};

const ENDPOINT = {
	/** Adds one or more line-items to the cart and returns JSON. */
	ADD: '/cart/add.js',
};

/* ----------------------------------------------------------------------- */
/*  Core helpers                                                           */
/* ----------------------------------------------------------------------- */

/**
 * Fetch wrapper that resolves with parsed JSON or throws with a descriptive
 * error. Mirrors the pattern used in productForm.js / cartDrawer.js.
 */
async function fetchJSON(url, opts = {}) {
	const defaults = {
		credentials: 'same-origin',
		headers: {
			'Content-Type': 'application/json',
			'X-Requested-With': 'XMLHttpRequest',
		},
	};
	const res = await fetch(url, { ...defaults, ...opts });
	const data = await res.json().catch(() => ({}));
	if (!res.ok) {
		throw new Error(data?.description || data?.message || `${res.status} ${res.statusText}`);
	}
	return data;
}

/**
 * Extract `KEY` from a line-item property input name, e.g.
 *   items[][properties][_event_id]  ->  _event_id
 *   properties[_event_id]           ->  _event_id
 *
 * @param {string} name
 * @returns {string|null}
 */
function propKey(name) {
	const m = name.match(/\[properties\]\[(.+?)\]$/) || name.match(/^properties\[(.+?)\]$/);
	return m ? m[1] : null;
}

/**
 * Collect form-level line-item properties. Evey keeps a top-level
 * `properties[_event_id]` alongside the per-line copies; we use these as
 * fallback defaults in case any per-line value is blank.
 *
 * @param {HTMLFormElement} form
 * @returns {Object<string,string>}
 */
function collectFormProps(form) {
	const props = {};
	form.querySelectorAll(SEL.formProps).forEach((input) => {
		const key = propKey(input.name);
		const val = (input.value || '').trim();
		if (key && val) props[key] = val;
	});
	return props;
}

/**
 * Build a Shopify `items` array from the Evey form, skipping any row with a
 * zero/blank quantity. Per-line properties win over form-level fallbacks.
 *
 * @param {HTMLFormElement} form
 * @returns {Array<{id:string, quantity:number, properties?:Object}>}
 */
function buildItems(form) {
	const formProps = collectFormProps(form);
	const items = [];

	form.querySelectorAll(SEL.variantContainer).forEach((container) => {
		const id = container.querySelector(SEL.variantId)?.value;
		const qty = parseInt(container.querySelector(SEL.variantQty)?.value || '0', 10);
		if (!id || !Number.isFinite(qty) || qty <= 0) return;

		const lineProps = {};
		container.querySelectorAll(SEL.lineProps).forEach((input) => {
			const key = propKey(input.name);
			const val = (input.value || '').trim();
			if (key && val) lineProps[key] = val;
		});

		const properties = { ...formProps, ...lineProps };
		const item = { id, quantity: qty };
		if (Object.keys(properties).length) item.properties = properties;
		items.push(item);
	});

	return items;
}

/* ----------------------------------------------------------------------- */
/*  Inline error messaging                                                 */
/* ----------------------------------------------------------------------- */

function showEveyError(form, message) {
	let box = form.querySelector('[data-evey-cart-error]');
	if (!box) {
		box = document.createElement('p');
		box.setAttribute('data-evey-cart-error', '');
		box.setAttribute('role', 'alert');
		box.setAttribute('aria-live', 'polite');
		box.style.cssText = 'color:#c12c41;margin-top:10px;';
		(form.querySelector(SEL.messageArea) || form).appendChild(box);
	}
	box.textContent = message;
}

function clearEveyError(form) {
	form.querySelector('[data-evey-cart-error]')?.remove();
}

/* ----------------------------------------------------------------------- */
/*  Submit handler                                                         */
/* ----------------------------------------------------------------------- */

/**
 * Intercept the Evey ticket form submission and route it through AJAX.
 *
 * @param {SubmitEvent} e
 */
async function handleEveySubmit(e) {
	const form = e.target.closest(SEL.eveyForm);
	if (!form) return;

	/* Evey runs its own date/quantity validation in a form-level submit
	   listener (which fires before this delegated one). If it already blocked
	   the submit, respect that and let Evey show its own messaging. */
	if (e.defaultPrevented) return;

	/* From here we own the submission — never let it navigate to /cart. */
	e.preventDefault();

	/* Guard against double submits while a request is in flight. */
	if (form.dataset.eveyAjaxLoading === 'true') return;

	clearEveyError(form);

	const items = buildItems(form);
	if (!items.length) {
		showEveyError(form, 'Please choose a date and at least one ticket before adding to cart.');
		return;
	}

	const btn = e.submitter || form.querySelector(SEL.submitBtn);
	const label = btn?.querySelector('span');
	const prevLabel = label?.textContent;
	const wasDisabled = btn?.disabled ?? false;

	form.dataset.eveyAjaxLoading = 'true';
	if (btn) btn.disabled = true;
	if (label) label.textContent = 'Adding…';

	try {
		await fetchJSON(ENDPOINT.ADD, {
			method: 'POST',
			body: JSON.stringify({ items }),
		});

		await updateCartUI();
		openDrawer(document.querySelector(SEL.drawer));
	} catch (err) {
		console.error('Evey add to cart failed:', err);
		showEveyError(form, err.message || 'Sorry, we could not add these tickets. Please try again.');
	} finally {
		form.dataset.eveyAjaxLoading = 'false';
		if (btn) btn.disabled = wasDisabled;
		if (label && prevLabel != null) label.textContent = prevLabel;
	}
}

/* ----------------------------------------------------------------------- */
/*  Public API                                                             */
/* ----------------------------------------------------------------------- */

/**
 * Attach the Evey cart behaviour to `document`.
 *
 * A delegated listener is used (rather than binding the form directly) because
 * Evey injects its form asynchronously after the page loads — delegation means
 * we catch the submit no matter when the form appears.
 */
export function initEveyCart() {
	document.addEventListener('submit', handleEveySubmit);
}

// Self-initialise on DOM ready, matching productForm.js.
document.addEventListener('DOMContentLoaded', initEveyCart);
